import React, { useState, useMemo, useCallback } from 'react'
import { Typography, Select, Popover } from 'antd'
import { PrescriptionItem } from './PrescriptionItem'
import {
    parseTemplateVariables,
    splitTemplateParts,
    matchTemplatePart,
    templateHasUnresolvedVariables,
} from './pharmacyNotesUtils'
import { isCdsVariable } from '../../../../utils/pharmacyNoteUtils'
import {
    PharmacyNotesContainer,
    NotesRow,
    NotesLabel,
    VariablesCard,
    VariablesCardHint,
    VariableRow,
    VariableLabel,
    FilledToken,
    UnfilledToken,
    PreviewText,
    CompoundIngredientRow,
    ScheduleIndicator,
    PrescriptionContainer,
    MedicationHeader,
    MedicationTitle,
    UnresolvedWarning,
    PrescriptionGrid,
    ReadyToSignBox,
    SignCheckbox,
    VariableInput,
    VariableSelect,
} from './Prescription.styles'

const { Text } = Typography

const CompoundIngredient = ({ ingredient }) => {
    return (
        <CompoundIngredientRow key={ingredient.freeText || ingredient.name}>
            <div>{ingredient.freeText || ingredient.name},</div>
            <div>{ingredient.quantity} {ingredient.dispenseUnit}</div>
        </CompoundIngredientRow>
    )
}

const renderCompoundIngredients = ingredients => {
    const ingredientComponents = ingredients.map((ingredient, index) => 
        <CompoundIngredient key={ingredient.freeText || ingredient.name || index} ingredient={ingredient} />
    )
    return <PrescriptionItem style={{ gridColumnStart: 1, gridColumnEnd: 3, textTransform: 'none' }} label='Ingredients' value={ingredientComponents} />
}

const Medication = ({ prescription }) => {
    return (
        <div>
            { prescription.name || prescription.drugName }
            { !!prescription.schedule && prescription.schedule !== "0" && <ScheduleIndicator> (Schedule {prescription.schedule})</ScheduleIndicator> }
        </div>
    )
}

const formatDate = (date) => {
    if (!date) return 'N/A'
    try {
        return new Date(date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        })
    } catch {
        return date
    }
}

const TemplatePreview = ({ template, values = {} }) => {
    const parts = splitTemplateParts(template).filter(Boolean)
    return (
        <PreviewText>
            {parts.map((part, idx) => {
                const varName = matchTemplatePart(part)
                if (varName !== null) {
                    const filled = values[varName]?.trim()
                    return filled
                        ? <FilledToken key={idx}>{filled}</FilledToken>
                        : <UnfilledToken key={idx}>{part}</UnfilledToken>
                }
                return <span key={idx}>{part}</span>
            })}
        </PreviewText>
    )
}

const PharmacyNotesField = ({ notes, variableValues = {}, onVariableChange, submitAttempted = false, cdsOptions = [] }) => {
    const [touched, setTouched] = useState(new Set())
    const variables = useMemo(() => parseTemplateVariables(notes), [notes])

    const handleBlur = useCallback((variableName) => {
        setTouched(prev => new Set([...prev, variableName]))
    }, [])

    if (!variables.length) {
        return (
            <PrescriptionItem
                style={{ gridColumnStart: 1, gridColumnEnd: 3, textTransform: 'none' }}
                label='Notes to Pharmacy'
                value={notes || 'None'}
            />
        )
    }

    return (
        <PharmacyNotesContainer>
            <NotesRow>
                <NotesLabel>Notes to Pharmacy</NotesLabel>
                <TemplatePreview template={notes} values={variableValues} />
            </NotesRow>
            <VariablesCard>
                <VariablesCardHint>Please fill in required pharmacy notes values:</VariablesCardHint>
                {variables.map(variable => (
                    <VariableRow key={variable}>
                        <VariableLabel>{variable}</VariableLabel>
                        {isCdsVariable(variable) && cdsOptions.length > 0 ? (
                            <VariableSelect
                                size="small"
                                placeholder="Select CDS option"
                                value={variableValues[variable] || undefined}
                                onChange={value => onVariableChange?.(variable, value)}
                                onBlur={() => handleBlur(variable)}
                                status={(touched.has(variable) || submitAttempted) && !variableValues[variable]?.trim() ? 'error' : ''}
                            >
                                {cdsOptions.map(option => (
                                    <Select.Option key={option} value={option}>
                                        <Popover content={option} mouseEnterDelay={0.4} overlayStyle={{ zIndex: 2000 }}>
                                            <span>{option}</span>
                                        </Popover>
                                    </Select.Option>
                                ))}
                            </VariableSelect>
                        ) : (
                            <VariableInput
                                size="small"
                                placeholder={`Enter ${variable.toLowerCase()}`}
                                value={variableValues[variable] || ''}
                                onChange={e => onVariableChange?.(variable, e.target.value)}
                                onBlur={() => handleBlur(variable)}
                                status={(touched.has(variable) || submitAttempted) && !variableValues[variable]?.trim() ? 'error' : ''}
                            />
                        )}
                    </VariableRow>
                ))}
            </VariablesCard>
        </PharmacyNotesContainer>
    )
}

export const Prescription = ({ prescription, i, onReadyToSign, variableValues = {}, onVariableChange, submitAttempted = false, cdsOptions = [] }) => {
    const isControlledSubstance = prescription.schedule && prescription.schedule !== "0"
    const hasUnresolvedVariables = useMemo(
        () => templateHasUnresolvedVariables(prescription.pharmacyNotes, variableValues),
        [prescription.pharmacyNotes, variableValues]
    )

    const handleVariableChange = useCallback((variableName, value) => {
        onVariableChange?.(prescription._id, variableName, value)
    }, [prescription._id, onVariableChange])

    const handleReadyToSignChange = useCallback(({ target }) => {
        onReadyToSign?.(prescription._id, target.checked)
    }, [onReadyToSign, prescription._id])
    
    return (
        <PrescriptionContainer>
            <MedicationHeader>
                <MedicationTitle>Medication {i + 1}</MedicationTitle>
                {hasUnresolvedVariables && (
                    <UnresolvedWarning>⚠️ Fill required pharmacy notes fields</UnresolvedWarning>
                )}
            </MedicationHeader>
            <PrescriptionGrid>
                <PrescriptionItem label='Drug Description' value={<Medication prescription={prescription}/>}/>
                <PrescriptionItem label='Dosage' value={prescription.strength} />
                {!!prescription.ingredients?.length && renderCompoundIngredients(prescription.ingredients)}
                <PrescriptionItem label='Quantity' value={`${prescription.quantity} ${prescription.quantityUnits || prescription.dispenseUnit || 'unit(s)'}`} />
                <PrescriptionItem label='Dispense Type' value={prescription.doseForm || prescription.dispense || 'N/A'} />
                <PrescriptionItem label='Days Supply' value={prescription.daysSupply} />
                <PrescriptionItem label='Refills' value={prescription.refills} />
                <PrescriptionItem label='Substitutions' value={`${prescription.noSubstitutions ? 'No' : ''} Substitutions Allowed`} />
                <PrescriptionItem label='Urgent' value={prescription.isUrgent ? 'Yes' : 'No'} />
                <PrescriptionItem label='Effective Date' value={formatDate(prescription.startDate)} />
                <PharmacyNotesField
                    notes={prescription.pharmacyNotes}
                    variableValues={variableValues}
                    onVariableChange={handleVariableChange}
                    submitAttempted={submitAttempted}
                    cdsOptions={cdsOptions}
                />
                <PrescriptionItem style={{ gridColumnStart: 1, gridColumnEnd: 3, textTransform: 'none' }} label='Directions' value={prescription.directions} />
                <PrescriptionItem style={{ gridColumnStart: 1, gridColumnEnd: 3, textTransform: 'none' }} label='Pharmacy' value={prescription.pharmacy?.name} />
            </PrescriptionGrid>
            { !!isControlledSubstance && (
                <ReadyToSignBox>
                    <Text>Ready to sign?</Text>
                    <SignCheckbox size="large" checked={prescription.readyToSign} onChange={handleReadyToSignChange} />
                </ReadyToSignBox>
            )}
        </PrescriptionContainer>
    )
}
